import { PasswordInput } from "@/components/password-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { AlertCircle, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import GoogleLoginButton from "./GoogleLoginButton";
import ZaloLoginButton from "./ZaloLoginButton";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"form">) {
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isZaloLoading, setIsZaloLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  // Helper function để lấy callbackUrl
  const getCallbackUrl = useCallback(() => {
    // Ưu tiên 1: callbackUrl từ query string
    const searchParams = new URLSearchParams(location.search);
    const callbackUrl = searchParams.get("callbackUrl");
    if (callbackUrl) {
      // Decode URL để xử lý các ký tự đặc biệt
      return decodeURIComponent(callbackUrl);
    }
    // Ưu tiên 2: location.state?.from?.pathname
    if (location.state?.from?.pathname) {
      return location.state.from.pathname;
    }
    // Ưu tiên 3: mặc định
    return "/dashboard";
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(email, password, rememberMe);

      if (result.success) {
        const callbackUrl = getCallbackUrl();
        // Sử dụng setTimeout để đợi state được cập nhật trước khi navigate
        // Điều này đảm bảo Router không redirect về /dashboard trước khi navigate đến callbackUrl
        setTimeout(() => {
          window.location.href = callbackUrl;
        }, 100);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.log(err);
      setError("Đã xảy ra lỗi, vui lòng thử lại!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className={cn(
        "flex flex-col gap-6 p-6 bg-white rounded-lg border border-gray-200",
        className
      )}
      onSubmit={handleSubmit}
      {...props}
    >
      <div className="flex flex-col gap-2">
        <p className="">Đăng nhập,</p>
        <h1 className="text-gray-800 text-2xl font-bold">
          Hệ thống <span className="font-bold text-primary">ABT</span>
        </h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Nhập email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="grid gap-3">
          <div className="flex items-center">
            <Label htmlFor="password">Mật khẩu</Label>
          </div>
          <PasswordInput
            id="password"
            placeholder="Nhập mật khẩu"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Ghi nhớ tài khoản
            </label>
          </div>
          <Link
            to="/forgot-password"
            className="text-sm text-primary underline"
          >
            Quên mật khẩu
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Đang đăng nhập...
            </>
          ) : (
            "Đăng nhập"
          )}
        </Button>
        <div className="flex items-center justify-center gap-2">
          <span className="text-muted-foreground  w-full h-[1px] bg-gray-200"></span>
          <span className="text-gray-500 text-sm text-nowrap flex-1 ">
            Đăng nhập bằng
          </span>
          <span className="text-muted-foreground w-full h-[1px] bg-gray-200"></span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ZaloLoginButton
            rememberMe={rememberMe}
            isZaloLoading={isZaloLoading}
            setIsZaloLoading={setIsZaloLoading}
            setError={setError}
            disabled={isLoading || isGoogleLoading || isZaloLoading}
            callbackUrl={getCallbackUrl()}
          />
          <GoogleLoginButton
            rememberMe={rememberMe}
            setIsGoogleLoading={setIsGoogleLoading}
            setError={setError}
            disabled={isLoading || isGoogleLoading || isZaloLoading}
            callbackUrl={getCallbackUrl()}
          />
        </div>
        <div className=" text-sm">
          <span className="text-muted-foreground">
            Nếu bạn chưa có tài khoản hãy đăng ký{" "}
          </span>
          <Link to="/register" className="font-medium  underline text-primary">
            tại đây
          </Link>
        </div>
      </div>
    </form>
  );
}
